import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Clock, Layers, ChevronRight,
  Search, Plus
} from "lucide-react";
import { toast } from "sonner";
import { useAuth, useUser, UserButton } from "@clerk/clerk-react";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

const AnalysisCard = ({ analysis, onClick }) => {
  const date = new Date(analysis.created_at);
  const formattedDate = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -3 }}
      onClick={onClick}
      className="glass-card rounded-2xl p-6 cursor-pointer hover:border-gold-500/30 transition-all group"
      data-testid={`history-card-${analysis.id}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gold-gradient flex items-center justify-center">
            <Layers className="w-5 h-5 text-black" />
          </div>
          <div>
            <span className="font-secondary font-bold text-xs tracking-widest uppercase text-gold-400">
              {analysis.classification?.app_type || "App"}
            </span>
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-zinc-500 group-hover:text-gold-400 transition-colors" />
      </div>

      <h3 className="font-primary font-semibold text-lg text-white mb-2 line-clamp-2">
        {analysis.idea}
      </h3>

      <div className="flex items-center gap-4 text-sm text-zinc-500">
        <div className="flex items-center gap-1">
          <Clock className="w-4 h-4" />
          {formattedDate}
        </div>
        <div className="flex items-center gap-1">
          <Layers className="w-4 h-4" />
          {analysis.tech_stack?.length || 0} technologies
        </div>
      </div>
    </motion.div>
  );
};

export default function HistoryPage() {
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { user } = useUser();

  useEffect(() => {
    const fetchAnalyses = async () => {
      try {
        const token = await getToken();
        const response = await fetch(`${API}/analyses`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          const data = await response.json();
          setAnalyses(data);
        }
      } catch (error) {
        console.error("Failed to fetch analyses:", error);
        toast.error("Failed to load history");
      } finally {
        setLoading(false);
      }
    };

    fetchAnalyses();
  }, [getToken]);

  const handleViewAnalysis = (analysis) => {
    sessionStorage.setItem("analysisResult", JSON.stringify(analysis));
    navigate("/results");
  };

  const filteredAnalyses = analyses.filter(a =>
    a.idea.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.classification?.app_type?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-obsidian-300 relative">
      {/* Main Content */}
      <main className="max-w-[1200px] mx-auto px-6 py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-zinc-400 hover:text-gold-400 transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
            New Analysis
          </button>
          <span className="font-secondary font-bold text-sm tracking-[0.3em] uppercase text-gold-400">
            StackVibecode
          </span>
        </div>

        {/* User Info & Title */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <span className="text-sm text-zinc-500 uppercase tracking-wider">Your Analyses</span>
            <h1 className="font-primary font-bold text-2xl md:text-3xl text-white mt-2">
              Analysis History
            </h1>
          </div>

          {user && (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <UserButton afterSignOutUrl="/" />
                <div className="text-right">
                  <p className="text-white font-medium">{user.fullName || user.firstName}</p>
                  <p className="text-xs text-zinc-500">{user.primaryEmailAddress?.emailAddress}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Search */}
        <div className="mb-8">
          <div className="relative max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search analyses..."
              className="w-full bg-white/5 border border-white/10 rounded-xl pl-12 pr-4 py-3 text-white placeholder:text-zinc-500 focus:outline-none focus:border-gold-500/50 transition-all"
              data-testid="search-input"
            />
          </div>
        </div>

        {/* Analyses Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-gold-500/30 border-t-gold-500 rounded-full animate-spin" />
          </div>
        ) : filteredAnalyses.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 rounded-2xl bg-white/5 flex items-center justify-center mx-auto mb-4">
              <Layers className="w-10 h-10 text-zinc-600" />
            </div>
            <h3 className="font-primary font-semibold text-xl text-white mb-2">
              {searchQuery ? "No matching analyses" : "No analyses yet"}
            </h3>
            <p className="text-zinc-500 mb-6">
              {searchQuery
                ? "Try a different search term"
                : "Start by analyzing your first app idea"
              }
            </p>
            {!searchQuery && (
              <button
                onClick={() => navigate("/")}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl gold-gradient text-black font-semibold hover:shadow-gold transition-all"
                data-testid="new-analysis-btn"
              >
                <Plus className="w-5 h-5" />
                New Analysis
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAnalyses.map((analysis, index) => (
              <AnalysisCard
                key={analysis.id}
                analysis={analysis}
                onClick={() => handleViewAnalysis(analysis)}
              />
            ))}
          </div>
        )}
      </main>

      {/* StackVibecode Footer Banner */}
      <div className="w-full bg-gold-500 py-24 md:py-32 lg:py-40">
        <p className="text-center font-secondary font-bold text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl tracking-[0.15em] uppercase text-black">
          StackVibecode
        </p>
      </div>
    </div>
  );
}
